import React, { useEffect, useState } from 'react';

export interface BankSearchProcessingProps {
  pollerState: 'idle' | 'polling' | 'slow' | 'timeout' | 'completed' | 'error';
  errorMessage: string | null;
  onRetry: () => void;
  searchTitle: string | null;
}

const MESSAGES = [
  'Reading the role…',
  'Extracting skills…',
  "Mapping your talent pool's wording…"
];

export const BankSearchProcessing: React.FC<BankSearchProcessingProps> = ({
  pollerState,
  errorMessage,
  onRetry,
  searchTitle
}) => {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    if (pollerState === 'polling') {
      const interval = setInterval(() => {
        setMsgIndex((prev) => (prev + 1) % MESSAGES.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [pollerState]);

  if (pollerState === 'idle' || pollerState === 'completed') {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-slate-200 shadow-sm text-center">
      {searchTitle && <h3 className="text-base font-bold text-slate-800 mb-6">{searchTitle}</h3>}
      
      {pollerState === 'error' && (
        <div className="flex flex-col items-center text-red-600 max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="mb-4 text-sm font-medium text-slate-700">{errorMessage || 'An error occurred during processing'}</p>
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-xs font-bold shadow-sm"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            Try Again
          </button>
        </div>
      )}

      {pollerState === 'timeout' && (
        <div className="flex flex-col items-center text-amber-600 max-w-md">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="mb-4 text-sm font-medium text-slate-700">Processing timed out. Please try again.</p>
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all text-xs font-bold shadow-sm"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            Try Again
          </button>
        </div>
      )}

      {(pollerState === 'polling' || pollerState === 'slow') && (
        <div className="flex flex-col items-center py-6">
          <div className="w-10 h-10 border-3 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4" />
          <p className="text-sm font-medium text-slate-600 animate-pulse">
            {pollerState === 'slow' 
              ? 'Still working — this is taking longer than usual…' 
              : MESSAGES[msgIndex]}
          </p>
        </div>
      )}
    </div>
  );
};
